import { createStyles, IconButton, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { Redirect, Router } from '@reach/router';
import { SnackbarProvider } from 'notistack';
import React from 'react';

import AppLoadingContainer from '../components/AppLoadingContainer';
import PageHeader from '../components/Header';
import { useAuth } from '../contexts/AuthContext/AuthContext';
import ClassifiersProvider from '../contexts/ClassifiersContext/ClassifiersProvider';
import { ROUTES } from '../routes';
import PrivateRoute from '../shared/PrivateRoute';
import BenchmarkListPage from './Benchmark';
import BenchmarkPage from './Benchmark/BenchmarkPage';
import BenchwiseListPage from './Benchmark/BenchwiseListPage';
import { BenchmarkContextProvider } from './Benchmark/Context/BenchmarkContext';
import BenchwisePage from './Benchwise';
import CompaniesPage from './FinancialData/Companies';
import CompanyPageInRefinementWrapper from './FinancialData/Company/CompanyPageInRefinementWrapper';
import CompanyPageWrapper from './FinancialData/Company/CompanyPageWrapper';
import CompanyConnectionsPage from './FinancialData/Company/Connections/CompanyChart/CompanyConnectionsPage';
import OwnershipConnectionsPage from './FinancialData/Company/Connections/OwnershipChart/OwnershipConnectionsPage';
import { CompaniesContextProvider } from './FinancialData/shared/CompanyContext/CompanyContext';
import { ErrorsContextProvider } from './FinancialData/shared/ErrorContext/ErrorContext';
import Help from './HelpPage';
import QueryListAdminPage from './QueryList/QueryListAdminPage';
import QueryListPage from './QueryList/QueryListPage';

export interface LinkState {
  companyDetailsPath: string;
}

const RootPage: React.FC = () => {
  const { isLoading, user, login, canLogin } = useAuth();
  const classes = useStyles();
  const notistackRef: any = React.createRef();
  const onClickDismiss = (key: any) => () => {
    notistackRef.current.closeSnackbar(key);
  };

  if (isLoading) {
    return <AppLoadingContainer />;
  }

  if (!user && canLogin) {
    login({
      appState: {
        targetUrl: window.location.pathname,
      },
    });

    return null;
  }

  return (
    <ClassifiersProvider>
      <SnackbarProvider
        maxSnack={5}
        ref={notistackRef}
        action={(key) => (
          <IconButton aria-label="close" color="inherit" onClick={onClickDismiss(key)}>
            <CloseIcon />
          </IconButton>
        )}
        classes={{
          variantInfo: classes.info,
        }}
      >
        <CompaniesContextProvider>
          <ErrorsContextProvider>
            <BenchmarkContextProvider>
              <PageHeader />
              {user?.canAccess ? (
                <Router>
                  <PrivateRoute path={ROUTES.financialData.page} component={CompaniesPage} />
                  <PrivateRoute
                    path={ROUTES.financialData.ownershipChart}
                    component={OwnershipConnectionsPage}
                  />
                  <PrivateRoute
                    path={ROUTES.financialData.companyChart}
                    component={CompanyConnectionsPage}
                  />
                  <PrivateRoute
                    path={ROUTES.financialData.company}
                    component={CompanyPageWrapper}
                  />
                  <PrivateRoute
                    path={ROUTES.financialData.legacyOwnershipChart}
                    component={OwnershipConnectionsPage}
                  />
                  <PrivateRoute
                    path={ROUTES.financialData.legacyCompanyChart}
                    component={CompanyConnectionsPage}
                  />
                  <PrivateRoute
                    path={ROUTES.financialData.legacyCompany}
                    component={CompanyPageWrapper}
                  />
                  <PrivateRoute path={ROUTES.mySearches.page} component={QueryListPage} />
                  <PrivateRoute path={ROUTES.allSearches.page} component={QueryListAdminPage} />
                  <PrivateRoute path={ROUTES.help.page} component={Help} />
                  <PrivateRoute path={ROUTES.benchmarking.page} component={BenchmarkListPage} />
                  <PrivateRoute path={ROUTES.benchmarking.benchmark} component={BenchmarkPage} />
                  <PrivateRoute path={ROUTES.benchmarking.benchmarkTab} component={BenchmarkPage} />
                  <PrivateRoute path={ROUTES.benchmarking.benchwise} component={BenchwisePage} />
                  <PrivateRoute
                    path={ROUTES.benchmarking.benchwiseStudyList}
                    component={BenchwiseListPage}
                  />
                  <PrivateRoute
                    path={ROUTES.benchmarking.refinementCompany}
                    component={CompanyPageInRefinementWrapper}
                  />
                  <PrivateRoute
                    path={ROUTES.benchmarking.connections}
                    component={CompanyConnectionsPage}
                  />
                  <PrivateRoute
                    path={ROUTES.benchmarking.ownership}
                    component={OwnershipConnectionsPage}
                  />
                  <Redirect from="/*" to={ROUTES.financialData.page} noThrow />
                </Router>
              ) : (
                <Router>
                  <Redirect from="*" to={ROUTES.help.page} noThrow />
                  <PrivateRoute path={ROUTES.help.page} component={Help} />
                </Router>
              )}
            </BenchmarkContextProvider>
          </ErrorsContextProvider>
        </CompaniesContextProvider>
      </SnackbarProvider>
    </ClassifiersProvider>
  );
};

const useStyles = makeStyles((theme) =>
  createStyles({
    info: {
      backgroundColor: theme.palette.primary.dark + '!important',
    },
  }),
);

export default RootPage;
